import loadable from '@loadable/component';
import { Box, Button, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';

import { RoleGuard } from '@components/Guards';
import Icon from '@components/Icon/Icon';

import { RoleName } from '@constants/role_name';
import { DISCERN, DISCERN_JOBS } from '@constants/routes';

import MainWrapper from '@views/Records/Common/MainWrapper';

/**
 * react-pdf sets up the pdfjs worker at module scope and needs a DOM, and the
 * editor only renders once the query params validate. Loading it lazily keeps
 * pdfjs out of this page's initial chunk and off the SSR path.
 */
const PdfSplitContainer = loadable(
  () => import('@components/PdfSplitEditor/PdfSplitContainer'),
  { ssr: false }
);

const messages = defineMessages({
  back: {
    id: 'discernSplitEditor.back',
    defaultMessage: 'Back',
  },
  binaryRequired: {
    id: 'discernSplitEditor.binaryRequired',
    defaultMessage:
      'Required: binaryId (upload must be completed before opening split editor).',
  },
  discernDashboardLink: {
    id: 'discernSplitEditor.discernDashboardLink',
    defaultMessage: 'Go to Discern Dashboard',
  },
  missingParams: {
    id: 'discernSplitEditor.missingParams',
    defaultMessage: 'Missing required parameters',
  },
  missingPatientId: {
    id: 'discernSplitEditor.missingPatientId',
    defaultMessage: 'Missing patient ID',
  },
  navigate: {
    id: 'discernSplitEditor.navigate',
    defaultMessage:
      'Please navigate to this page from the upload flow or document table.',
  },
  patientName: {
    id: 'discernSplitEditor.patientName',
    defaultMessage: 'Patient {name}',
  },
  requiredPatientId: {
    id: 'discernSplitEditor.requiredPatientId',
    defaultMessage: 'Patient ID is required.',
  },
  title: {
    id: 'discernSplitEditor.title',
    defaultMessage: 'PDF Split Editor',
  },
});

function Content() {
  const { formatMessage } = useIntl();

  const vitallPatientId = useSearchParam('vitallPatientId');
  // For existing documents (when navigating from table), we might have documentId
  const discernDocumentId = useSearchParam('discernDocumentId');
  const binaryId = useSearchParam('binaryId'); // For new uploads after completion or existing documents

  // Validate required parameters
  // binaryId is required (upload is completed before navigating here)
  // discernDocumentId is optional - only needed when navigating from existing document table
  if (!binaryId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">
          {formatMessage(messages.missingParams)}
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {formatMessage(messages.binaryRequired)}
          <br />
          {formatMessage(messages.navigate)}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(DISCERN)}
          sx={{ mt: 2 }}
        >
          {formatMessage(messages.discernDashboardLink)}
        </Button>
      </Box>
    );
  }

  if (!vitallPatientId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">
          {formatMessage(messages.missingPatientId)}
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {formatMessage(messages.requiredPatientId)}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(DISCERN)}
          sx={{ mt: 2 }}
        >
          {formatMessage(messages.discernDashboardLink)}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Button
        fullWidth={false}
        startIcon={<Icon name="arrow-left" type="fas" />}
        onClick={() => {
          if (vitallPatientId) {
            navigate(DISCERN_JOBS);
          } else {
            navigate(DISCERN);
          }
        }}
        sx={{ width: 'fit-content' }}
      >
        {formatMessage(messages.back)}
      </Button>

      {/* Split Editor Container */}
      <Box sx={{ overflow: 'hidden' }}>
        <PdfSplitContainer
          binaryId={binaryId || undefined}
          vitallPatientId={vitallPatientId}
          discernDocumentId={discernDocumentId || undefined}
        />
      </Box>
    </Box>
  );
}

export default function SplitEditorPage() {
  const { formatMessage } = useIntl();

  return (
    <RoleGuard roles={[RoleName.Admin, RoleName.Curator]}>
      <MainWrapper title={formatMessage(messages.title)} hideTitle={true}>
        <Content />
      </MainWrapper>
    </RoleGuard>
  );
}
